// Normalises GET /assistants/:id/overview into what the dashboard cards render.
//
// Every metric arrives as { value, previousValue, changePct }. The backend
// works out the comparison itself, so the cards show its percentage rather
// than deriving a second one that could disagree with it.

const firstOf = (source, keys) => {
  if (!source) return null;

  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null) return value;
  }

  return null;
};

const firstArrayOf = (source, keys) => {
  const value = firstOf(source, keys);
  return Array.isArray(value) ? value : [];
};

const numberOr = (value) => (typeof value === "number" ? value : null);

// Tolerates a bare number as well, so a metric the backend later flattens
// still renders instead of falling back to a dash.
const readMetric = (metric) => {
  if (metric && typeof metric === "object") {
    return {
      value: numberOr(metric.value),
      previousValue: numberOr(metric.previousValue),
      changePct: numberOr(metric.changePct),
    };
  }

  return { value: numberOr(metric), previousValue: null, changePct: null };
};

export const formatNumber = (value) =>
  typeof value === "number" ? value.toLocaleString() : "—";

export const formatPercent = (value) => {
  if (typeof value !== "number") return "—";
  // Accepts either 0.86 or 86.
  const percent = value > 0 && value <= 1 ? value * 100 : value;
  return `${Math.round(percent)}%`;
};

export const formatSeconds = (value) => {
  if (typeof value !== "number") return "—";
  // Anything this large is milliseconds, not seconds.
  const seconds = value > 1000 ? value / 1000 : value;
  return `${seconds.toFixed(1)}s`;
};

// Trailing ".0" reads as false precision on a whole-number change.
export const formatChange = (value) => {
  if (typeof value !== "number") return null;

  const rounded = Math.round(Math.abs(value) * 10) / 10;
  const magnitude = Number.isInteger(rounded) ? rounded : rounded.toFixed(1);

  return `${value < 0 ? "−" : "+"}${magnitude}%`;
};

// Chart points arrive as {date,count}-ish pairs; both halves have aliases.
const readPoint = (point) => ({
  date: firstOf(point, ["date", "day", "label", "bucket", "timestamp"]),
  value: firstOf(point, ["count", "value", "total", "conversations"]) ?? 0,
});

const readTopic = (topic) => ({
  label: firstOf(topic, ["topic", "label", "name", "title"]) || "Other",
  percent: firstOf(topic, ["percent", "percentage", "share", "value"]) ?? 0,
});

const readConversation = (conversation) => ({
  id: firstOf(conversation, ["id", "_id", "conversationId"]),
  name:
    firstOf(conversation, ["name", "visitorName", "visitor", "user", "title"]) ||
    "Visitor",
  detail:
    firstOf(conversation, [
      "detail",
      "summary",
      "lastMessage",
      "preview",
      "topic",
    ]) || "",
  time: firstOf(conversation, ["time", "createdAt", "updatedAt", "lastMessageAt"]),
  status: firstOf(conversation, ["status", "state"]),
});

export const mapOverview = (raw) => {
  if (!raw) return null;

  return {
    // The window the numbers actually cover, which the API echoes back.
    from: raw.from || null,
    to: raw.to || null,

    totalConversations: readMetric(raw.totalConversations),
    leads: readMetric(raw.leadsCaptured),
    resolutionRate: readMetric(raw.resolutionRate),
    avgResponseTime: readMetric(raw.avgResponseTimeSeconds),

    // Not part of the overview payload today. The reads stay alias-based so
    // these light up on their own if the endpoint grows them; until then the
    // cards below show their empty states.
    series: firstArrayOf(raw, [
      "conversationsOverTime",
      "series",
      "timeline",
      "chart",
      "daily",
      "byDay",
    ]).map(readPoint),

    topics: firstArrayOf(raw, [
      "topTopics",
      "topics",
      "conversationTopics",
    ]).map(readTopic),

    recentConversations: firstArrayOf(raw, [
      "recentConversations",
      "recent",
      "conversationsList",
      "latestConversations",
    ]).map(readConversation),
  };
};
